import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Tabs,
  Tab,
  Card,
  CardBody,
} from '@heroui/react';
import { toast } from 'react-toastify';
import {
  AssetCategory,
  AssetType,
  FieldDefinition,
  CreateAssetCategoryDto,
  UpdateAssetCategoryDto,
} from 'shared/types/asset';
import { FieldDefinitionBuilder } from './FieldDefinitionBuilder';
import { AssetTypeBuilder } from './AssetTypeBuilder';
import { FileRequirementsBuilder, FileRequirementItem } from './FileRequirementsBuilder';
import {
  useCreateAssetCategory,
  useUpdateAssetCategory,
  useCreateFieldDefinition,
  useUpdateFieldDefinition,
  useDeleteFieldDefinition,
  useCreateAssetType,
  useUpdateAssetType,
  useDeleteAssetType,
  useAssetCategory,
  useAssetFileRequirements,
} from 'hooks/useAssets';

interface AssetCategoryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  category?: AssetCategory;
  mode: 'create' | 'edit';
}

export const AssetCategoryForm: React.FC<AssetCategoryFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  category,
  mode,
}) => {
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    displayOrder: number;
    active: boolean;
  }>({
    name: '',
    description: '',
    displayOrder: 0,
    active: true,
  });

  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [types, setTypes] = useState<AssetType[]>([]);
  const [fileRequirements, setFileRequirements] = useState<FileRequirementItem[]>([]);
  const [originalFieldIds, setOriginalFieldIds] = useState<number[]>([]);
  const [originalTypeIds, setOriginalTypeIds] = useState<number[]>([]);

  const { createCategory, isCreating } = useCreateAssetCategory();
  const { updateCategory, isUpdating } = useUpdateAssetCategory();
  const { createFieldDefinition } = useCreateFieldDefinition();
  const { updateFieldDefinition } = useUpdateFieldDefinition();
  const { deleteFieldDefinition } = useDeleteFieldDefinition();
  const { createAssetType } = useCreateAssetType();
  const { updateAssetType } = useUpdateAssetType();
  const { deleteAssetType } = useDeleteAssetType();

  // Fetch full category data with relations when editing
  const shouldFetchCategory = mode === 'edit' && category?.id;
  const {
    category: fetchedCategory,
    isLoading: isFetchingCategory
  } = useAssetCategory(shouldFetchCategory ? category.id.toString() : '');

  // Fetch file requirements for category when editing
  const { requirements: fetchedRequirements } = useAssetFileRequirements(
    shouldFetchCategory ? category.id.toString() : ''
  );

  // Use fetched category in edit mode (has relations), otherwise use prop
  const categoryData = mode === 'edit' && fetchedCategory ? fetchedCategory : category;

  const isLoading = isCreating || isUpdating || (mode === 'edit' && isFetchingCategory);

  useEffect(() => {
    if (categoryData && mode === 'edit') {
      setFormData({
        name: categoryData.name,
        description: categoryData.description || '',
        displayOrder: categoryData.displayOrder,
        active: categoryData.active,
      });
      // Convert AssetFieldDefinition (numeric IDs) to FieldDefinition (string IDs)
      setFields((categoryData.fieldDefinitions || []).map(field => ({
        id: field.id?.toString(),
        assetCategoryId: field.assetCategoryId?.toString(),
        fieldName: field.fieldName,
        displayName: field.displayName,
        fieldType: field.fieldType,
        isRequired: field.isRequired,
        displayOrder: field.displayOrder,
        validationRules: field.validationRules,
        enumValues: field.enumValues,
        metadata: field.metadata,
        fileLogConnection: field.fileLogConnection,
        isActive: field.active,
      })));
      setTypes(categoryData.assetTypes || []);
      setFileRequirements((fetchedRequirements || []).map((req, index) => ({
        id: parseInt(req.id),
        fileLabelId: parseInt(req.fileLabelId),
        fileLabel: req.fileLabel ? {
          id: parseInt(req.fileLabel.id),
          label: req.fileLabel.label
        } : undefined,
        requirementType: req.requirementType as 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL',
        description: req.description,
        active: req.active,
        displayOrder: index
      })));

      // Track original IDs for deletion detection
      setOriginalFieldIds((categoryData.fieldDefinitions || []).map(f => f.id).filter((id): id is number => id !== undefined));
      setOriginalTypeIds((categoryData.assetTypes || []).map(t => t.id).filter((id): id is number => id !== undefined));
    } else if (mode === 'create') {
      setFormData({
        name: '',
        description: '',
        displayOrder: 0,
        active: true,
      });
      setFields([]);
      setTypes([]);
      setFileRequirements([]);
      setOriginalFieldIds([]);
      setOriginalTypeIds([]);
    }
  }, [categoryData, mode, isOpen, fetchedRequirements]);

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Voer een categorienaam in');
        return;
      }

      if (mode === 'create') {
        // Create category
        const categoryDto: CreateAssetCategoryDto = {
          name: formData.name,
          description: formData.description || undefined,
          displayOrder: formData.displayOrder,
        };

        const createdCategory = await createCategory(categoryDto);

        // Create field definitions
        for (const field of fields) {
          await createFieldDefinition({
            assetCategoryId: createdCategory.id.toString(),
            fieldName: field.fieldName,
            displayName: field.displayName,
            fieldType: field.fieldType,
            isRequired: field.isRequired,
            displayOrder: field.displayOrder,
            validationRules: field.validationRules,
            enumValues: field.enumValues,
            metadata: field.metadata,
            fileLogConnection: field.fileLogConnection,
          });
        }

        // Create asset types
        for (const type of types) {
          await createAssetType({
            assetCategoryId: createdCategory.id,
            name: type.name,
            description: type.description,
            scopes: type.scopes,
            displayOrder: type.displayOrder,
          });
        }

        // TODO: File requirements are now type-level, not category-level.
        // Category-level file requirements have been deprecated.
        // Use AssetCategoryEditorPage to manage file requirements per type.

        toast.success('Voorziening categorie succesvol aangemaakt');
      } else if (category) {
        // Update category
        const categoryDto: UpdateAssetCategoryDto = {
          name: formData.name,
          description: formData.description || undefined,
          displayOrder: formData.displayOrder,
          active: formData.active,
        };

        await updateCategory(category.id.toString(), categoryDto);

        // Handle field definitions
        for (const field of fields) {
          const fieldIdNum = field.id ? parseInt(field.id) : undefined;
          if (fieldIdNum && originalFieldIds.includes(fieldIdNum)) {
            // Update existing field
            await updateFieldDefinition(category.id.toString(), field.id!, {
              fieldName: field.fieldName,
              displayName: field.displayName,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              displayOrder: field.displayOrder,
              validationRules: field.validationRules,
              enumValues: field.enumValues,
              metadata: field.metadata,
              fileLogConnection: field.fileLogConnection,
            });
          } else if (!fieldIdNum || !originalFieldIds.includes(fieldIdNum)) {
            // Create new field
            await createFieldDefinition({
              assetCategoryId: category.id.toString(),
              fieldName: field.fieldName,
              displayName: field.displayName,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              displayOrder: field.displayOrder,
              validationRules: field.validationRules,
              enumValues: field.enumValues,
              metadata: field.metadata,
              fileLogConnection: field.fileLogConnection,
            });
          }
        }

        // Delete removed field definitions
        const currentFieldIds = fields
          .map(f => f.id ? parseInt(f.id) : undefined)
          .filter((id): id is number => id !== undefined);
        const deletedFieldIds = originalFieldIds.filter(id => !currentFieldIds.includes(id));
        for (const fieldId of deletedFieldIds) {
          await deleteFieldDefinition(category.id.toString(), fieldId.toString());
        }

        // Handle asset types
        for (const type of types) {
          if (type.id && originalTypeIds.includes(type.id)) {
            // Update existing type
            await updateAssetType(category.id.toString(), type.id.toString(), {
              name: type.name,
              description: type.description,
              scopes: type.scopes,
              displayOrder: type.displayOrder,
            });
          } else if (!type.id || !originalTypeIds.includes(type.id)) {
            // Create new type
            await createAssetType({
              assetCategoryId: category.id,
              name: type.name,
              description: type.description,
              scopes: type.scopes,
              displayOrder: type.displayOrder,
            });
          }
        }

        // Delete removed asset types
        const currentTypeIds = types
          .map(t => t.id)
          .filter((id): id is number => id !== undefined);
        const deletedTypeIds = originalTypeIds.filter(id => !currentTypeIds.includes(id));
        for (const typeId of deletedTypeIds) {
          await deleteAssetType(category.id.toString(), typeId.toString());
        }

        toast.success('Voorziening categorie succesvol bijgewerkt');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving asset category:', error);
      toast.error(
        error instanceof Error ? error.message : 'Kan voorziening categorie niet opslaan'
      );
    }
  };

  const handleClose = () => {
    setActiveTab('basic');
    onClose();
  };

  // Step navigation
  const steps = [
    { key: 'basic', label: 'Basisinformatie', number: 1 },
    { key: 'types', label: 'Voorziening Types', number: 2 },
    { key: 'fields', label: 'Aangepaste Velden', number: 3 },
    { key: 'file-requirements', label: 'Bestandsvereisten', number: 4 },
  ];

  const currentStepIndex = steps.findIndex(step => step.key === activeTab);
  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Validation for step 1
  const canProceedFromBasicInfo = formData.name.trim().length > 0;

  // Navigation handlers
  const handleNext = () => {
    if (currentStepIndex === 0 && !canProceedFromBasicInfo) {
      toast.error('Vul eerst een categorienaam in om door te gaan');
      return;
    }

    if (!isLastStep) {
      const nextStep = steps[currentStepIndex + 1];
      setActiveTab(nextStep.key);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      const prevStep = steps[currentStepIndex - 1];
      setActiveTab(prevStep.key);
    }
  };

  // In edit mode, allow free navigation. In create mode, guide through steps
  const canNavigateToTab = (stepKey: string) => {
    if (mode === 'edit') return true;

    const stepIndex = steps.findIndex(s => s.key === stepKey);
    // Can only navigate to current or previous steps in create mode
    return stepIndex <= currentStepIndex;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        base: 'max-h-[90vh]',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <div className="w-full">
            <h2 className="text-xl font-semibold mb-4">
              {mode === 'create' ? 'Voorziening Categorie Aanmaken' : 'Voorziening Categorie Bewerken'}
            </h2>

            {/* Progress Indicator - Only show in create mode */}
            {mode === 'create' && (
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-moss-primary">
                    Stap {currentStep.number} van {steps.length}
                  </span>
                  <span className="text-xs text-neutral-primary/60">
                    {currentStep.label}
                  </span>
                </div>
                <div className="flex gap-2">
                  {steps.map((step, index) => (
                    <div
                      key={step.key}
                      className={`h-2 flex-1 rounded-full transition-colors ${
                        index <= currentStepIndex
                          ? 'bg-moss-primary'
                          : 'bg-neutral-primary/10'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody>
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => {
              const targetKey = key as string;
              if (canNavigateToTab(targetKey)) {
                setActiveTab(targetKey);
              } else {
                toast.warning('Voltooi eerst de vorige stappen om verder te gaan');
              }
            }}
            className="mb-4"
            disabledKeys={mode === 'create' ? steps.filter(s => !canNavigateToTab(s.key)).map(s => s.key) : []}
          >
            <Tab key="basic" title="Basisinformatie">
              <div className="space-y-4 py-4">
                {/* Help card for basic info */}
                <Card className="bg-moss-50 border border-moss-200">
                  <CardBody className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">📋</div>
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-moss-900 mb-2">
                          Stap 1: Definieer de voorziening categorie
                        </p>
                        <p className="text-moss-800 mb-2">
                          Wat is een voorziening categorie? Een categorie groepeert gelijkaardige voorzieningen samen.
                        </p>
                        <p className="text-moss-700 text-xs mb-2">
                          <strong>Voorbeelden van generieke categorieën:</strong>
                          <br />• <strong>Elektrische Toestellen</strong> → types: oven, koelkast, wasmachine, droogkast
                          <br />• <strong>HVAC Systemen</strong> → types: verwarmingsketel, airconditioning, boiler
                          <br />• <strong>Sanitair</strong> → types: toilet, douche, kraan, wastafel
                          <br />• <strong>Liften & Roltrappen</strong> → types: personenlift, goederenlift, roltrap
                        </p>
                        <p className="text-moss-600 text-xs italic">
                          💡 Het bereik (gebouw/unit/kamer) stel je in per type in stap 2.
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Input
                  label="Categorienaam"
                  placeholder="bijv. Elektrische Toestellen, Sanitair, HVAC Systemen, Liften"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  isRequired
                  size="lg"
                />

                <Textarea
                  label="Beschrijving"
                  placeholder="Beschrijf deze voorziening categorie..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="text-xs text-gray-500 mt-1">
                      Inactieve categorieën zijn niet zichtbaar bij het aanmaken van nieuwe voorzieningen
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, active: !formData.active })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      formData.active ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <Input
                  type="number"
                  label="Weergavevolgorde"
                  description="Lagere nummers verschijnen eerst in lijsten"
                  value={formData.displayOrder.toString()}
                  onChange={(e) =>
                    setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
            </Tab>

            <Tab key="types" title="Voorziening Types">
              <div className="py-4">
                {/* Help card for types */}
                <Card className="bg-blue-50 border border-blue-200 mb-4">
                  <CardBody className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">🏷️</div>
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-blue-900 mb-2">
                          Stap 2: Definieer de types binnen deze categorie
                        </p>
                        <p className="text-blue-800 mb-2">
                          Wat zijn voorziening types? Types zijn de specifieke varianten binnen een categorie.
                        </p>
                        <p className="text-blue-700 text-xs mb-2">
                          <strong>Voorbeelden van types binnen categorieën:</strong>
                          <br />• Categorie "Elektrische Toestellen" → Oven, Koelkast, Wasmachine, Droogkast
                          <br />• Categorie "Sanitair" → Toilet, Douche, Kraan, Wastafel
                          <br />• Categorie "HVAC Systemen" → Verwarmingsketel, Airconditioning, Ventilatie
                        </p>
                        <p className="text-blue-600 text-xs italic">
                          Later kun je deze types toewijzen aan specifieke gebouwen/units.
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <AssetTypeBuilder types={types} onChange={setTypes} />
              </div>
            </Tab>

            <Tab key="fields" title="Aangepaste Velden">
              <div className="py-4">
                {/* Help card for fields */}
                <Card className="bg-purple-50 border border-purple-200 mb-4">
                  <CardBody className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">⚙️</div>
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-purple-900 mb-2">
                          Stap 3: Definieer aangepaste velden voor deze categorie
                        </p>
                        <p className="text-purple-800 mb-2">
                          Wat zijn aangepaste velden? Extra informatie die je wilt bijhouden voor elke voorziening.
                        </p>
                        <p className="text-purple-700 text-xs mb-2">
                          <strong>Voorbeelden voor "Elektrische toestellen":</strong>
                          <br />• Serienummer (tekst, verplicht)
                          <br />• Aanschafdatum (datum)
                          <br />• Garantie vervaldatum (datum)
                          <br />• Vermogen (getal, eenheid: Watt)
                          <br />• Energielabel (dropdown: A+++, A++, A+, A, B, C, D)
                        </p>
                        <p className="text-purple-600 text-xs italic">
                          Deze velden verschijnen later bij het registreren van specifieke voorzieningen.
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <FieldDefinitionBuilder
                  fields={fields}
                  onChange={setFields}
                  categoryId={category?.id}
                  availableFileLabels={fileRequirements
                    .filter(req => req.fileLabel?.label)
                    .map(req => req.fileLabel!.label)}
                />
              </div>
            </Tab>

            <Tab key="file-requirements" title="Bestandsvereisten">
              <div className="py-4">
                <FileRequirementsBuilder requirements={fileRequirements} onChange={setFileRequirements} />
              </div>
            </Tab>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="light" onPress={handleClose}>
            Annuleren
          </Button>

          {mode === 'create' ? (
            <>
              {/* Previous button - show if not on first step */}
              {!isFirstStep && (
                <Button color="default" onPress={handlePrevious}>
                  Vorige
                </Button>
              )}

              {/* Next button - show if not on last step */}
              {!isLastStep ? (
                <Button
                  color="primary"
                  onPress={handleNext}
                  isDisabled={currentStepIndex === 0 && !canProceedFromBasicInfo}
                >
                  Volgende
                </Button>
              ) : (
                /* Create button - only show on last step */
                <Button color="primary" onPress={handleSubmit} isLoading={isLoading}>
                  Categorie Aanmaken
                </Button>
              )}
            </>
          ) : (
            /* Edit mode - simple save button */
            <Button color="primary" onPress={handleSubmit} isLoading={isLoading}>
              Wijzigingen Opslaan
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
